import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { getTotalRevenueAmount } from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import {
  collectRevenueCurrencies,
  resolveRevenueCurrency,
} from "@/components/pages/analytics/front-desk/components/revenue/revenueCurrency";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import type { RevenueSlice } from "../types/analyticsDashboard";
import type { AdminHotelMetricRow } from "../types/adminHotelMetricTable";
import {
  formatPeriodLabel,
  getPeriodContextKey,
} from "../utils/analyticsDashboardDate";
import {
  DEFAULT_TREND,
  getRevenueTrend,
  mapToRevenueSlices,
} from "../utils/revenueFormatting";
import { mapTenantRevenueRowsToMetricRowsWithComparison } from "../utils/adminRevenueMappers";
import { useAdminTenantId } from "./useAdminTenantId";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchAdminRevenueData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
) {
  const rangeQuery = { from, to };
  const prevRangeQuery = { from: previousFrom, to: previousTo };

  const [
    currentTable,
    previousTable,
    currentTotal,
    previousTotal,
    hotelOptions,
  ] = await Promise.all([
    analyticsTenantService.getRevenueTable(rangeQuery),
    analyticsTenantService.getRevenueTable(prevRangeQuery),
    analyticsTenantService.getRevenueTotal(rangeQuery),
    analyticsTenantService.getRevenueTotal(prevRangeQuery),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const tableRows = mapTenantRevenueRowsToMetricRowsWithComparison({
    currentRows: currentTable.rows,
    previousRows: previousTable.rows,
    hotelLogosById,
  });

  const revenueDistribution = mapToRevenueSlices(
    currentTable.rows.map((row) => ({
      hotelId: row.hotelId,
      hotelName: row.hotelName,
      totalRevenue: row.totalRevenue / (row.amountDivisor || 1),
      currency: row.currency ?? "EUR",
    })),
  );

  const totalRevenue = getTotalRevenueAmount(currentTotal);
  const previousTotalRevenue = getTotalRevenueAmount(previousTotal);
  const currency = resolveRevenueCurrency(
    collectRevenueCurrencies(currentTotal),
  );

  return {
    tableRows,
    revenueDistribution,
    totalRevenue,
    currency,
    revenueTrend: getRevenueTrend(totalRevenue, previousTotalRevenue),
  };
}

export const adminRevenueKeys = {
  all: ["admin-analytics-revenue"] as const,
  hotels: (tenantId: string, from: string, to: string) =>
    [...adminRevenueKeys.all, tenantId, from, to] as const,
};

export function useAdminRevenueHotels(dateRange: DateRange) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminRevenueKeys.hotels(tenantId ?? "", from, to),
    queryFn: () =>
      fetchAdminRevenueData(tenantId!, from, to, previousFrom, previousTo),
    enabled: isReady,
  });

  const tableRows = useMemo<AdminHotelMetricRow[]>(
    () => query.data?.tableRows ?? [],
    [query.data?.tableRows],
  );

  const revenueDistribution = useMemo<RevenueSlice[]>(
    () => query.data?.revenueDistribution ?? [],
    [query.data?.revenueDistribution],
  );

  const totalRevenue = query.data?.totalRevenue ?? 0;
  const currency = query.data?.currency ?? "EUR";
  const revenueTrend = query.data?.revenueTrend ?? DEFAULT_TREND;
  const totalRevenueRounded = Number(totalRevenue.toFixed(2));
  const periodLabel = formatPeriodLabel(dateRange);
  const periodContextKey = getPeriodContextKey(dateRange);
  const hotelCount = revenueDistribution.length;

  return {
    tenantId,
    tableRows,
    revenueDistribution,
    totalRevenue,
    totalRevenueRounded,
    currency,
    periodLabel,
    periodContextKey,
    revenueTrend,
    hotelCount,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
